import { Link, f7 } from 'framework7-react';
import { FC, useEffect, useState } from 'react';
import { RegisterMethod } from './RegisterMethod/RegisterMethod';
import style from './RegisterQRPageStyle.module.css';
import { validateAffiliateSignature } from '@/api/registerQRApi';
import CustomerIcon from '@/assets/images/register-qr/customer.png';
import LogoImg from '@/assets/images/register-qr/logo.svg';
import StaffIcon from '@/assets/images/register-qr/staff.png';
import { RegisterQRPageTemplate } from '@/components/uiParts/Template/RegisterQRPageTemplate/RegisterQRPageTemplate';
import { initialState } from '@/config/initialState';
import { paths } from '@/config/paths';
import { useToast } from '@/hooks/useToast';
import { storeDispatch } from '@/utils/store';

export const RegisterQRPage: FC = () => {
  const { openToast } = useToast();
  const [isValidating, setIsValidating] = useState(false);
  const [isSignatureValid, setIsSignatureValid] = useState<boolean | null>(
    null,
  );

  const redirectInviteCode = () => {
    f7.view.main.router.navigate(paths.registerNewMember);
  };

  const redirectRegisterNewStaff = () => {
    f7.view.main.router.navigate(paths.registerNewStaff);
  };

  useEffect(() => {
    const initializeRegistration = async () => {
      const { affiliate_id, sig } = f7.view.main.router.currentRoute.query;
      const affiliateIdNum = Number(affiliate_id);

      // QRコード経由の場合（affiliate_idとsigが両方ある場合）は署名検証を実行
      if (affiliate_id && sig) {
        setIsValidating(true);
        try {
          f7.preloader.show();
          const response = await validateAffiliateSignature({
            affiliate_id: affiliateIdNum,
            sig: sig as string,
          });

          if (!response.data.success || !response.data.valid) {
            openToast(
              response.data.error?.message || '無効なQRコードです',
              'toast-failed',
            );
            setIsSignatureValid(false);
            // エラー時はログインチュートリアル画面に戻す
            f7.view.main.router.navigate(paths.loginTutorial, {
              animate: false,
            });
            return;
          }
          setIsSignatureValid(true);
        } catch (error) {
          openToast('QRコードの検証に失敗しました', 'toast-failed');
          setIsSignatureValid(false);
          f7.view.main.router.navigate(paths.loginTutorial, {
            animate: false,
          });
          return;
        } finally {
          setIsValidating(false);
          f7.preloader.hide();
        }
      } else {
        // sigがない場合は従来通り（既存の動作を維持）
        setIsSignatureValid(true);
      }

      // Reset data in store
      storeDispatch('setRegisterUser', 'registerUser', {
        ...initialState.registerUser,
        isCorporation: false,
        currentStep: 1,
        step1: {
          companyName: '',
          companyNameKana: '',
          firstName: '',
          firstNameKana: '',
          familyName: '',
          familyNameKana: '',
          email: '',
          birthday: '',
          tel1: '',
          tel2: '',
          tel3: '',
          tel4: '',
          tel5: '',
          tel6: '',
          mobile_type1: 0,
          mobile_type2: null,
        },
        step2: {
          zipCode1: '',
          zipCode2: '',
          municipalities: '',
          prefectures: '',
          street: '',
          apartment: '',
        },
        affiliate_id: affiliateIdNum || 1,
        // QR経由の場合のみ署名を保持し、登録リクエストに載せてサーバーで検証させる
        sig: (sig as string) || undefined,
      });
    };

    initializeRegistration();
  }, [openToast]);

  // 署名検証中はローディング表示
  if (isValidating) {
    return (
      <RegisterQRPageTemplate
        title=""
        pageName="RegisterQR"
        showNavBack={false}
      >
        <div className={style['register-qr-div']}>
          <img src={LogoImg} />
          <p>QRコードを検証中...</p>
        </div>
      </RegisterQRPageTemplate>
    );
  }

  // 署名検証失敗時はエラー表示
  if (isSignatureValid === false) {
    return (
      <RegisterQRPageTemplate
        title=""
        pageName="RegisterQR"
        showNavBack={false}
      >
        <div>
          <Link
            onClick={() => {
              f7.views.main.router.navigate(paths.loginTutorial, {
                animate: false,
              });
            }}
            className={`${style['no-ripple']} no-ripple`}
          >
            <div>
              <i className="icon-left no-ripple" />
            </div>
          </Link>
          {/* register-qr-div は flex なので、ロゴと並べる見出しは1つに留める */}
          <div className={style['register-qr-div']}>
            <img src={LogoImg} />
            無効なQRコードです
          </div>
          <p className={style['register-qr-error-message']}>
            正しいQRコードを読み取ってください
          </p>
        </div>
      </RegisterQRPageTemplate>
    );
  }

  return (
    <RegisterQRPageTemplate title="" pageName="RegisterQR" showNavBack={false}>
      <div>
        <Link
          onClick={() => {
            // backを使うとログアウト後にログインチュートリアルのページに戻れないのでnavigateを使う
            f7.views.main.router.navigate(paths.loginTutorial, {
              animate: false,
            });
          }}
          className={`${style['no-ripple']} no-ripple`}
        >
          <div>
            <i className="icon-left no-ripple" />
          </div>
        </Link>
        <div className={style['register-qr-div']}>
          <img src={LogoImg} />
          新規会員
        </div>
        <div className={style['register-qr-method']}>
          <RegisterMethod
            handleClick={redirectInviteCode}
            img={CustomerIcon}
            title="個人のお客様"
          />
          <RegisterMethod
            handleClick={redirectRegisterNewStaff}
            img={StaffIcon}
            title="法人のお客様"
          />
        </div>
      </div>
    </RegisterQRPageTemplate>
  );
};
